import React from 'react';
import { Calendar as CalendarIcon, ArrowUpDown } from 'lucide-react';
import { DateInput } from './DateInput';

/**
 * Full catalogue of presets supported by this component.
 * Order here = visual order in the pill bar.
 *
 * `configurable` marks presets that the Service Settings → Field Visibility
 * admin can individually toggle off. The others (`tomorrow`, `last7`) are
 * always visible per the iter-109 spec.
 */
const ALL_PRESETS = [
  { id: 'yesterday', label: 'Yesterday',    configurable: true  },
  { id: 'today',     label: 'Today',        configurable: true  },
  { id: 'tomorrow',  label: 'Tomorrow',     configurable: false },
  { id: 'last7',     label: 'Last 7 Days',  configurable: false },
  { id: 'wtd',       label: 'Week-to-Date', configurable: true  },
  { id: 'mtd',       label: 'Month-to-Date',configurable: true  },
  { id: 'qtd',       label: 'Quarter-to-Date', configurable: true },
  { id: 'ytd',       label: 'Year-to-Date', configurable: true  },
  { id: 'custom',    label: 'Custom',       configurable: true  },
];

const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;

export const computePresetRange = (id) => {
  const t = new Date();
  const d = (offset) => { const x = new Date(t); x.setDate(x.getDate() + offset); return x; };

  // Indian fiscal year starts April 1. Month index is 0-based (Jan=0 … Dec=11).
  // Fiscal Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar (same calendar year,
  // belongs to the PREVIOUS fiscal year that started the prior April).
  const fyStart = () => {
    const m = t.getMonth();
    // If we're on/after April, FY started April 1 of this year.
    // If we're Jan-Mar, the current FY started April 1 of LAST year.
    const year = m >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return new Date(year, 3, 1); // month 3 = April
  };

  const fiscalQuarterStart = () => {
    const m = t.getMonth();
    const y = t.getFullYear();
    if (m >= 3 && m <= 5) return new Date(y, 3, 1);   // Q1: Apr-Jun
    if (m >= 6 && m <= 8) return new Date(y, 6, 1);   // Q2: Jul-Sep
    if (m >= 9 && m <= 11) return new Date(y, 9, 1);  // Q3: Oct-Dec
    return new Date(y, 0, 1);                         // Q4: Jan-Mar (same calendar year)
  };

  const weekStart = () => {
    // Monday-start week
    const x = new Date(t);
    const day = x.getDay(); // 0=Sun .. 6=Sat
    const diff = day === 0 ? 6 : day - 1;
    x.setDate(x.getDate() - diff);
    return x;
  };
  switch (id) {
    case 'previous': // legacy alias kept for any stale callers
    case 'yesterday': return [fmt(d(-1)), fmt(d(-1))];
    case 'today':     return [fmt(t), fmt(t)];
    case 'tomorrow':  return [fmt(d(1)), fmt(d(1))];
    case 'last7':     return [fmt(d(-6)), fmt(t)];
    case 'wtd':       return [fmt(weekStart()), fmt(t)];
    case 'mtd':       return [fmt(new Date(t.getFullYear(), t.getMonth(), 1)), fmt(t)];
    case 'qtd':       return [fmt(fiscalQuarterStart()), fmt(t)];
    case 'ytd':       return [fmt(fyStart()), fmt(t)];
    case 'custom':    return [fmt(t), fmt(t)]; // no-op range; user edits inputs
    default:          return [fmt(t), fmt(t)];
  }
};

/**
 * DateRangePresets — preset pills + DD-MM-YYYY date inputs + sort toggle.
 *
 * Props:
 *   • visiblePresets:  optional object {today: true, yesterday: false, …}
 *                      When supplied, `configurable: true` presets are shown
 *                      only if their key is `true`. Non-configurable presets
 *                      (`tomorrow`, `last7`) always show.
 *                      When the prop is undefined (e.g. ReconciliationTab)
 *                      ALL presets are shown — fully backward compatible.
 *   • All other props unchanged from the original signature.
 */
export function DateRangePresets({
  activePreset, setActivePreset,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  sortDir, setSortDir,
  showSort = true,
  visiblePresets,
}) {
  const applyPreset = (id) => {
    setActivePreset(id);
    if (id === 'custom') {
      // Leave dates as-is — user will edit the date inputs directly
      return;
    }
    const [a, b] = computePresetRange(id);
    setDateFrom(a);
    setDateTo(b);
  };

  const presets = React.useMemo(() => {
    if (!visiblePresets) return ALL_PRESETS;
    return ALL_PRESETS.filter((p) => !p.configurable || visiblePresets[p.id] !== false);
  }, [visiblePresets]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => applyPreset(p.id)}
          data-testid={`preset-${p.id}`}
          className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium border transition-all
            ${activePreset === p.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700'}`}
        >
          {p.label}
        </button>
      ))}
      <span className="text-slate-300 px-1">·</span>
      <CalendarIcon size={14} className="text-slate-400" />
      <div className="w-44">
        <DateInput value={dateFrom} onChange={(v) => { setDateFrom(v); setActivePreset(''); }} testId="date-from" />
      </div>
      <span className="text-slate-400 text-[12.5px]">to</span>
      <div className="w-44">
        <DateInput value={dateTo} onChange={(v) => { setDateTo(v); setActivePreset(''); }} testId="date-to" />
      </div>
      {showSort && (
        <>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            data-testid="sort-toggle"
            className="px-3 py-1.5 rounded-full text-[12.5px] font-medium border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-700 inline-flex items-center gap-1.5"
          >
            <ArrowUpDown size={13} /> {sortDir === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </>
      )}
    </div>
  );
}

export default DateRangePresets;
